import { useCallback, useRef, useState } from "react";
import { AuthResponse } from "../../api/auth";
import {
  generateNonce,
  buildBridgeUrl,
  pollBridge,
} from "../../api/authBridge";
import { openLink } from "../../platform";

interface UseBridgeAuthResult {
  pending: "vk" | null;
  error: "timeout" | null;
  start: (provider: "vk") => void;
  cancel: () => void;
}

// Drives the Mini App side of the social bridge: open the system browser to the
// bridge page, poll for the session it produces, then hand it to onSession
// (which links Telegram + sets the app session). Survives the user leaving and
// returning to Telegram because the Mini App stays mounted.
export function useBridgeAuth(opts: {
  onSession: (session: AuthResponse) => void | Promise<void>;
}): UseBridgeAuthResult {
  const [pending, setPending] = useState<"vk" | null>(null);
  const [error, setError] = useState<"timeout" | null>(null);
  const cancelledRef = useRef(false);
  const onSessionRef = useRef(opts.onSession);
  onSessionRef.current = opts.onSession;

  const start = useCallback((provider: "vk") => {
    setError(null);
    cancelledRef.current = false;
    const nonce = generateNonce();
    setPending(provider);
    openLink(buildBridgeUrl(nonce, provider));
    pollBridge(nonce, { timeoutMs: 120000, isCancelled: () => cancelledRef.current })
      .then(async (session) => {
        if (session) {
          await onSessionRef.current(session);
        } else if (!cancelledRef.current) {
          setError("timeout");
        }
      })
      .catch((error) => {
        // onSession (or poll) failed after a session was obtained — log and
        // clear; the user has a stored session and can retry. Not a timeout.
        console.error("Telegram bridge onSession failed:", error);
      })
      .finally(() => setPending(null));
  }, []);

  const cancel = useCallback(() => {
    cancelledRef.current = true;
    setPending(null);
  }, []);

  return { pending, error, start, cancel };
}
